import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import { API } from "../context/ShopContext";
import { formatPrice } from "../mock";

const STATUS_LABELS = {
  paid: "Pagato",
  pending: "In attesa",
  cancelled: "Annullato",
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" }) +
    " " + d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
};

const OrderRow = ({ order, authHeaders, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const setStatus = async (status) => {
    if (status === order.status) return;
    if (!window.confirm(`Cambiare lo stato dell'ordine #${order.ref} in "${STATUS_LABELS[status]}"?`)) return;
    setBusy(true);
    try {
      await axios.put(`${API}/admin/orders/${order.order_id}/status`, { status }, authHeaders());
      toast.success("Stato aggiornato");
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  const c = order.customer || {};
  return (
    <div className="idm-admin-order" data-testid={`order-${order.ref}`}>
      <button className="idm-admin-order-head" onClick={() => setOpen(!open)} data-testid={`order-toggle-${order.ref}`}>
        <span className="idm-order-ref">#{order.ref}</span>
        <span className="idm-order-date">{fmtDate(order.created_at)}</span>
        <span className="idm-order-cust">{c.firstName} {c.lastName} — {c.email}</span>
        <span className="idm-order-total">{formatPrice(order.total)}</span>
        <span className={`idm-badge idm-badge-${order.status}`} data-testid={`order-status-${order.ref}`}>
          {STATUS_LABELS[order.status] || order.status}
        </span>
        {open ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
      </button>
      {open && (
        <div className="idm-admin-order-body" data-testid={`order-detail-${order.ref}`}>
          <div className="idm-order-items">
            {(order.items || []).map((it, i) => (
              <p key={i}>
                {it.qty} × {it.name?.it || it.name?.en || it.slug} — {formatPrice(it.price)}
                {it.subscription ? ` (abbonamento /${it.subscription.interval === "year" ? "anno" : "mese"})` : ""}
                {order.downloads && order.downloads[String(it.product_id)] != null
                  ? ` — download: ${order.downloads[String(it.product_id)]}`
                  : ""}
              </p>
            ))}
          </div>
          <div className="idm-order-meta">
            <p><strong>Lingua:</strong> {(order.lang || "it").toUpperCase()} | <strong>Tipo:</strong> {order.mode === "subscription" ? "Abbonamento" : "Una tantum"}</p>
            {c.address && <p><strong>Indirizzo:</strong> {c.address}, {c.zip} {c.city} ({c.country})</p>}
            {c.phone && <p><strong>Telefono:</strong> {c.phone}</p>}
            {order.consents && (
              <p>
                <strong>Consensi:</strong> Termini ✓
                {order.consents.withdrawal_waiver != null && ` | Rinuncia recesso ${order.consents.withdrawal_waiver ? "✓" : "✗"}`}
              </p>
            )}
            {order.paid_at && <p><strong>Pagato il:</strong> {fmtDate(order.paid_at)}</p>}
            {order.subscription_id && <p><strong>Stripe subscription:</strong> {order.subscription_id}</p>}
          </div>
          <div className="idm-order-actions">
            <label>
              Stato:
              <select
                value={order.status}
                disabled={busy}
                onChange={(e) => setStatus(e.target.value)}
                className="idm-select"
                data-testid={`order-status-select-${order.ref}`}
              >
                {Object.entries(STATUS_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </label>
          </div>
        </div>
      )}
    </div>
  );
};

const OrdersAdmin = ({ authHeaders }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/orders`, authHeaders());
      setOrders(data);
    } catch {
      toast.error("Errore caricamento ordini");
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = statusFilter ? orders.filter((o) => o.status === statusFilter) : orders;

  return (
    <section className="idm-admin-section" data-testid="admin-orders">
      <h3 className="idm-admin-section-title">Ordini</h3>
      <div className="idm-cat-filters" style={{ marginBottom: 18 }}>
        <button className={!statusFilter ? "active" : ""} onClick={() => setStatusFilter("")} data-testid="orders-filter-all">
          Tutti ({orders.length})
        </button>
        {Object.entries(STATUS_LABELS).map(([k, v]) => (
          <button key={k} className={statusFilter === k ? "active" : ""} onClick={() => setStatusFilter(k)} data-testid={`orders-filter-${k}`}>
            {v} ({orders.filter((o) => o.status === k).length})
          </button>
        ))}
      </div>
      {loading ? (
        <div className="idm-loading"><span className="idm-spinner" /></div>
      ) : filtered.length === 0 ? (
        <p className="idm-admin-section-sub">Nessun ordine trovato.</p>
      ) : (
        filtered.map((o) => (
          <OrderRow key={o.order_id} order={o} authHeaders={authHeaders} onChanged={load} />
        ))
      )}
    </section>
  );
};

export default OrdersAdmin;
